import { FC } from "react"

import { useTranslation } from "react-i18next"

import { Button } from "components/buttons/Button"

import { CioConnectionStatus } from "types/api"

import { ConnectionState } from "./types"

interface ApiStatusBadgeProps {
  label: string
  result: CioConnectionStatus["appApi"] | CioConnectionStatus["trackApi"] | undefined
}

const ApiStatusBadge: FC<ApiStatusBadgeProps> = ({ label, result }) => {
  if (!result) return null
  return result.ok ? (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
      <span className="text-base leading-none">✓</span> {label}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-red-500 dark:text-red-400">
      <span className="text-base leading-none">✗</span> {label}
      {"error" in result ? `: ${result.error}` : ""}
    </span>
  )
}

export interface ConnectionStatusBarProps {
  status: ConnectionState
  onTest: () => void
  testing: boolean
}

export const ConnectionStatusBar: FC<ConnectionStatusBarProps> = ({ status, onTest, testing }) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-2 border-b border-black/10 dark:border-white/10">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="font-medium text-gray-600 dark:text-gray-400">{t("settings.customerio.connection.label")}</span>
        {status === "loading" && <span className="text-xs text-gray-400 dark:text-gray-500">{t("settings.customerio.connection.checking")}</span>}
        {status === null && <span className="text-xs text-gray-400 dark:text-gray-500">{t("settings.customerio.connection.notTested")}</span>}
        {status !== null && status !== "loading" && !status.configured && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-yellow-600 dark:text-yellow-400">
            <span className="text-base leading-none">⚠</span> {t("settings.customerio.connection.noCredentials")}
          </span>
        )}
        {status !== null && status !== "loading" && status.configured && (
          <>
            <ApiStatusBadge label={t("settings.customerio.connection.trackApi")} result={status.trackApi} />
            <ApiStatusBadge label={t("settings.customerio.connection.appApi")} result={status.appApi} />
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {t("settings.customerio.connection.source", { source: status.credentialsSource })}
            </span>
          </>
        )}
      </div>
      <Button variant="ghost" size="xs" className="shrink-0 text-[rgba(169,78,206,1)]" onClick={onTest} loading={testing}>
        {t("settings.customerio.connection.test")}
      </Button>
    </div>
  )
}
